'use client';

import { useState, useCallback } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { PrimaryButton, SecondaryButton, DangerButton } from '@/components/platform/buttons';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { logger } from '@/lib/platform/utils/logger';

interface ImageCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete?: () => void;
  imageUrl: string;
  onCropComplete: (croppedImage: Blob) => void;
  title?: string;
  cropShape?: 'rect' | 'round';
  aspect?: number;
}

// Helper function to create cropped image
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous'; // Enable CORS to prevent canvas tainting
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  // Set canvas size to the cropped area
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // Detect if image has transparency
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const hasTransparency = imageData.data.some((_, i) => {
    // Check alpha channel (every 4th value starting at index 3)
    return i % 4 === 3 && imageData.data[i] < 255;
  });

  // Convert canvas to blob
  // Use PNG for transparent images, JPEG with compression for opaque images
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas is empty'));
        }
      },
      hasTransparency ? 'image/png' : 'image/jpeg',
      0.85
    );
  });
}

export default function ImageCropModal({
  isOpen,
  onClose,
  onDelete,
  imageUrl,
  onCropComplete,
  title = 'Crop Image',
  cropShape = 'round',
  aspect = 1,
}: ImageCropModalProps) {
  const { darkMode } = useDarkMode();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.85);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [loading, setLoading] = useState(false);

  const onCropChange = (location: { x: number; y: number }) => {
    setCrop(location);
  };

  const onZoomChange = (zoom: number) => {
    setZoom(zoom);
  };

  const onCropCompleteCallback = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleApply = async () => {
    if (!croppedAreaPixels) return;

    setLoading(true);
    try {
      const croppedImage = await getCroppedImg(imageUrl, croppedAreaPixels);
      onCropComplete(croppedImage);
    } catch (error) {
      logger.error('Error cropping image:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-[100]">
      <DialogBackdrop
        transition
        className={`fixed inset-0 backdrop-blur-[2px] transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in ${
          darkMode ? 'bg-gray-900/75' : 'bg-gray-500/75'
        }`}
      />

      <div className="fixed inset-0 z-[100] w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
          <DialogPanel
            transition
            className={`relative w-full transform overflow-hidden rounded-lg text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:max-w-[460px] data-closed:sm:translate-y-0 data-closed:sm:scale-95 ${
              darkMode ? 'border border-white/10 bg-gray-800' : 'bg-white'
            }`}
          >
            {/* Header */}
            <div className="${darkMode ? 'border-white/10' : 'border-gray-200'} flex items-center justify-between border-b px-6 py-4">
              <DialogTitle
                as="h3"
                className={`text-lg font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
              >
                {title}
              </DialogTitle>
              <button
                onClick={onClose}
                className={`rounded-md p-1 focus:ring-2 focus:ring-indigo-500 focus:outline-hidden ${
                  darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Crop Area */}
            <div className="relative" style={{ height: '400px' }}>
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                minZoom={0.5}
                aspect={aspect}
                cropShape={cropShape}
                showGrid={false}
                restrictPosition={false}
                onCropChange={onCropChange}
                onZoomChange={onZoomChange}
                onCropComplete={onCropCompleteCallback}
              />
            </div>

            {/* Zoom Slider */}
            <div className="px-6 py-4">
              <label
                className={`mb-2 block text-sm font-medium ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Zoom
              </label>
              <input
                type="range"
                min={0.5}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Footer */}
            <div className="flex justify-between gap-3 px-6 py-4">
              <DangerButton onClick={onDelete || onClose} disabled={loading}>
                Delete
              </DangerButton>
              <div className="flex gap-3">
                <SecondaryButton onClick={onClose} disabled={loading}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton onClick={handleApply} loading={loading}>
                  Apply
                </PrimaryButton>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
